import { useRender } from '@base-ui/react/use-render';
import type { MouseEvent, ReactElement } from 'react';
import { useToggleGroup } from '../containers/toggle-group.tsx';
import { cn } from '../util/classes.ts';

const baseClasses = cn(
    '[input]:appearance-none select-none inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap cursor-pointer',
    'relative isolate h-7 px-cozy rounded-default text-ui-base',
    'state-disabled:pointer-events-none state-disabled:opacity-disabled',
    'transition-colors duration-state ease-state',
    '[input[aria-label]]:after:content-[attr(aria-label)]',
    'bg-container-inset text-content-weak',
    'hover:text-content',
    // white tile floats above the grey track when checked
    "before:content-['']",
    'before:absolute before:inset-0.5 before:-z-10 before:rounded-[calc(var(--radius-default)-2px)] before:bg-container',
    'before:opacity-0 before:transition-opacity before:duration-state before:ease-state',
    'state-checked-within:before:opacity-100',
    'state-checked-within:text-content',
    '[&_input]:sr-only [&_input]:appearance-none',
    // joined siblings share one continuous rail: square the inner corners and
    // halve the tile's inset toward the join so a 4px grey gap remains
    'joined-leading:rounded-r-none joined-leading:before:right-0.5',
    'joined-trailing:rounded-l-none joined-trailing:before:left-0.5',
    // raise focus ring above neighbors
    'joined:focus-visible:relative joined:focus-visible:z-10',
);

export type InsetToggleButtonProps = useRender.ComponentProps<'button'> & {
    checked?: boolean;
    optionValue?: string;
};

export function InsetToggleButton({
    checked: checkedFromProps,
    className,
    optionValue,
    render,
    ...props
}: InsetToggleButtonProps): ReactElement {
    const [groupState, setGroupState] = useToggleGroup();
    const checkedFromGroup = groupState === undefined ? undefined : groupState === optionValue;
    const checked = checkedFromProps ?? checkedFromGroup;

    return useRender({
        defaultTagName: 'button',
        render,
        props: {
            'data-state': checked === true ? 'checked' : checked === false ? 'unchecked' : undefined,
            'data-value': optionValue,
            ...props,
            onClick: (e: MouseEvent<HTMLButtonElement>) => {
                props.onClick?.(e);
                if (!e.defaultPrevented) {
                    setGroupState(optionValue);
                }
            },
            className: cn(baseClasses, className),
        },
    });
}
